// Helpers to add an event or meet-up to a user's personal calendar.
// Supports Google Calendar (TEMPLATE link), Outlook Live (deeplink), and
// Apple Calendar / everything else via a downloadable .ics file.

function pad(n) { return String(n).padStart(2, "0"); }

function toUtcStamp(d) {
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

function dateOnlyStamp(iso) { return (iso || "").replace(/-/g, ""); }

function addDays(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}`;
}

// Parse a time string like "14:00", "2:00 pm", "2pm" into {hh, mm} in local time.
function parseLocalDateTime(dateIso, timeStr) {
  if (!dateIso) return null;
  const [y, mo, d] = dateIso.split("-").map(Number);
  const t = (timeStr || "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!m) return null;
  let hh = parseInt(m[1], 10);
  const mm = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = (m[3] || "").toLowerCase();
  if (ampm === "pm" && hh < 12) hh += 12;
  if (ampm === "am" && hh === 12) hh = 0;
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return new Date(y, mo - 1, d, hh, mm, 0);
}

export function calendarPayload(ev) {
  const title = ev.event_name || ev.title || "Compass Kids event";
  const location = ev.location || "";
  const description = ev.about_event || ev.description || "";
  const dateIso = ev.event_date || ev.date || "";
  const startTime = ev.event_time || ev.start_time || "";
  const endTime = ev.end_time || "";
  const startDt = startTime ? parseLocalDateTime(dateIso, startTime) : null;
  let endDt = null;
  if (startDt) {
    endDt = endTime ? parseLocalDateTime(dateIso, endTime) : null;
    if (!endDt || endDt <= startDt) {
      endDt = new Date(startDt.getTime() + 2 * 60 * 60 * 1000);
    }
  }
  return { title, location, description, dateIso, startDt, endDt };
}

export function googleCalendarUrl(ev) {
  const { title, location, description, dateIso, startDt, endDt } = calendarPayload(ev);
  const u = new URL("https://calendar.google.com/calendar/render");
  u.searchParams.set("action", "TEMPLATE");
  u.searchParams.set("text", title);
  if (startDt && endDt) {
    u.searchParams.set("dates", `${toUtcStamp(startDt)}/${toUtcStamp(endDt)}`);
  } else if (dateIso) {
    u.searchParams.set("dates", `${dateOnlyStamp(dateIso)}/${addDays(dateIso, 1)}`);
  }
  if (description) u.searchParams.set("details", description);
  if (location) u.searchParams.set("location", location);
  return u.toString();
}

export function outlookCalendarUrl(ev) {
  const { title, location, description, dateIso, startDt, endDt } = calendarPayload(ev);
  const u = new URL("https://outlook.live.com/calendar/0/deeplink/compose");
  u.searchParams.set("path", "/calendar/action/compose");
  u.searchParams.set("rru", "addevent");
  u.searchParams.set("subject", title);
  if (startDt && endDt) {
    u.searchParams.set("startdt", startDt.toISOString());
    u.searchParams.set("enddt", endDt.toISOString());
  } else if (dateIso) {
    u.searchParams.set("startdt", `${dateIso}T00:00:00`);
    u.searchParams.set("enddt", `${dateIso}T23:59:00`);
    u.searchParams.set("allday", "true");
  }
  if (description) u.searchParams.set("body", description);
  if (location) u.searchParams.set("location", location);
  return u.toString();
}

export function buildIcs(ev) {
  const { title, location, description, dateIso, startDt, endDt } = calendarPayload(ev);
  const now = new Date();
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}@compasskids`;
  const esc = (s = "") => String(s).replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Compass Kids//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toUtcStamp(now)}`,
  ];
  if (startDt && endDt) {
    lines.push(`DTSTART:${toUtcStamp(startDt)}`);
    lines.push(`DTEND:${toUtcStamp(endDt)}`);
  } else if (dateIso) {
    lines.push(`DTSTART;VALUE=DATE:${dateOnlyStamp(dateIso)}`);
    lines.push(`DTEND;VALUE=DATE:${addDays(dateIso, 1)}`);
  }
  lines.push(`SUMMARY:${esc(title)}`);
  if (location) lines.push(`LOCATION:${esc(location)}`);
  if (description) lines.push(`DESCRIPTION:${esc(description)}`);
  lines.push("END:VEVENT");
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

export function downloadIcs(ev) {
  const ics = buildIcs(ev);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const title = ev.event_name || ev.title || "event";
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/[^\w\-]+/g, "_").slice(0, 60) || "event"}.ics`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
}
